import {
	getUserProfileImageUrl,
	UserIdentityModel,
	UserPronounKind,
	UserStatus
} from "client/Api/UserProfile/service";
import {
	Button,
	DropOver,
	DropOverAlignmentValue,
	PersonAvatarStatusKind,
	PersonAvatarStatusValue
} from "client/Components";
import {
	ReactElement,
	useEffect,
	useMemo,
	useState
} from "react";
import { useValueEqualMemo } from "client/Hooks/useValueEqualMemo";
import {
	isNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import styles from "./UserDetailsBox.module.less";
import {
	getWorkspaceContactDetails,
	getWorkspaceContactIdFromUserId,
	WorkspaceContactDetailsModel
} from "client/Api/Email/contacts";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { AlertManager, useAlerts } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { ComputedDisplayNameResult, getComputedDisplayName } from "client/Features/AddressBook/common";
import { CommunicationButtons } from "client/Features/AddressBook/CommunicationButtons/CommunicationButtons";
import { PersonalInformationBanner } from "client/Features/PersonalInformationPage/PersonalInformationBanner";
import communicationButtonsStyles from "client/Features/AddressBook/CommunicationButtons/CommunicationButtons.module.less";
import classNames from "classnames";
import { presenceToAvatarStatusMap } from "client/Features/AddressBook/PersonCard/PersonCard";
import { getIdentifiesAs } from "client/Features/Settings/UserProfile/UserProfileForm";
import { never } from "client_legacy/ndaw/errors";

export type DetailsBoxUserInfo =
	| {
		kind: "userIdentity";
		userIdentity: UserIdentityModel;
	}
	| {
		kind: "contactDetails";
		contactDetails: WorkspaceContactDetailsModel;
	};

export type UserDetailsBoxProps = {
	target: HTMLElement;
	userInfo: DetailsBoxUserInfo;
	trackingId: string;
	onClose(): void;
	onMoreInfo(): void;
	targetAlignment?: DropOverAlignmentValue;
	contentAlignment?: DropOverAlignmentValue;
};

export const UserDetailsBox = (props: UserDetailsBoxProps): ReactElement | null => {
	const {
		target,
		userInfo: userInfoFromProps,
		trackingId,
		onClose,
		onMoreInfo,
		targetAlignment = "bl",
		contentAlignment = "br"
	} = props;

	const userInfo = useValueEqualMemo(
		(): DetailsBoxUserInfo => userInfoFromProps,
		[userInfoFromProps]
	);

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [fetchedDetails, setFetchedDetails] = useState<WorkspaceContactDetailsModel | null>(null);

	const alertManager: AlertManager = useAlerts();

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(userId: number, signal: AbortSignal): Promise<void> {
				setIsBusy(true);
				try {
					const details: WorkspaceContactDetailsModel = await getWorkspaceContactDetails(
						getWorkspaceContactIdFromUserId(userId),
						signal
					);

					setFetchedDetails(details);
					setIsBusy(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					setIsBusy(true);
				}
			}

			if (userInfo.kind === "contactDetails") {
				setFetchedDetails(null);
				return;
			}

			if (userInfo.userIdentity.status !== UserStatus.Active) {
				setFetchedDetails(null);
				return;
			}

			const cancellation = new AbortController();

			fetchData(
				userInfo.userIdentity
					.userId,
				cancellation.signal
			);

			return () => cancellation.abort();
		},
		[
			alertManager,
			userInfo
		]
	);

	const details = useMemo(
		(): WorkspaceContactDetailsModel | null => {
			if (userInfo.kind === "contactDetails") {
				return userInfo.contactDetails;
			}

			return fetchedDetails;
		},
		[userInfo, fetchedDetails]
	);

	const {
		firstName,
		lastName,
		jobTitle,
		department,
		officeLocation,
		profileImageUrl,
		pronouns
	} = useMemo(
		(): AlwaysAvailableUserDetails => {
			if (details) {
				return convertFullDetailsToAlwaysAvailableDetails(details);
			}

			return convertToAlwaysAvailableDetails(userInfo);
		},
		[details, userInfo]
	);

	const {
		initials,
		computedDisplayName
	} = useMemo(
		(): ComputedDisplayNameResult => {
			return getComputedDisplayName({
				firstName,
				lastName
			});
		},
		[firstName, lastName]
	);

	const info = useMemo(
		(): string | undefined => {
			const sections: string[] = [
				jobTitle,
				department,
				officeLocation
			]
				.filter(isNotNilOrEmpty);

			return sections.join(" | ");
		},
		[
			department,
			jobTitle,
			officeLocation
		]
	);

	const status = useMemo(
		(): PersonAvatarStatusValue | undefined => {
			if (!details) {
				return undefined;
			}

			const kind: PersonAvatarStatusKind | undefined = presenceToAvatarStatusMap[details.presenceStatus];

			if (isNil(kind)) {
				return undefined;
			}

			return {
				kind: "status",
				status: kind
			};
		},
		[details]
	);

	const renderActions = (): ReactElement | null => {
		if (details) {
			return (
				<CommunicationButtons
					emailAddress={details.emailAddress}
					phoneNumber={details.phone}
					variant="details"
				/>
			);
		}

		if (isBusy) {
			return <CommunicationButtonsSkeleton />;
		}

		return null;
	};

	const userStatus: UserStatus = useMemo(
		(): UserStatus => {
			switch (userInfo.kind) {
				case "contactDetails":
					// Its not possible to retrieve contact details for users not in active status
					return UserStatus.Active;
				case "userIdentity":
					return userInfo.userIdentity
						.status;
				default:
					never(userInfo);
			}
		},
		[userInfo]
	);

	const identifiesAs = useMemo(
		(): string | null => {
			if (userStatus === UserStatus.Inactive
				|| userStatus === UserStatus.Deleted
			) {
				return localize("_InactiveUser");
			}

			return getIdentifiesAs(pronouns);
		},
		[pronouns, userStatus]
	);

	return (
		<DropOver
			target={target}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			onClose={onClose}
			hasResponsiveDropOver={false}
		>
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
			>
				<PersonalInformationBanner
					trackingId="banner"
					fullName={computedDisplayName}
					identifiesAs={identifiesAs}
					avatarUrl={profileImageUrl ?? undefined}
					avatarPlaceholder={{
						kind: "initials",
						initials
					}}
					info={info}
					actions={renderActions()}
					backgroundImageUrl={details?.backgroundImageUrl ?? undefined}
					status={status}
				/>
				{userStatus === UserStatus.Active && (
					<div className={styles.moreDetails}>
						<Button
							width="fill"
							kind="secondary"
							text={localize("_MoreDetails")}
							onClick={onMoreInfo}
							trackingId="more-details"
						/>
					</div>
				)}
			</div>
		</DropOver>
	);
};
UserDetailsBox.displayName = "PersonalInformationDetailsBox";

const CommunicationButtonsSkeleton = (): ReactElement => {
	return (
		<div
			className={classNames(
				communicationButtonsStyles.root,
				communicationButtonsStyles.root_details
			)}
		>
			<div className={styles.skeletonButton} />
			<div className={styles.skeletonButton} />
			<div className={styles.skeletonButton} />
			<div className={styles.skeletonButton} />
		</div>
	);
};
CommunicationButtonsSkeleton.displayName = "CommunicationButtonsSkeleton";

type AlwaysAvailableUserDetails = {
	firstName: string | null;
	lastName: string | null;
	jobTitle: string | null;
	department: string | null;
	officeLocation: string | null;
	profileImageUrl: string | null;
	pronouns: UserPronounKind;
};

function convertToAlwaysAvailableDetails(userInfo: DetailsBoxUserInfo): AlwaysAvailableUserDetails {
	switch (userInfo.kind) {
		case "userIdentity":
			const {
				userIdentity
			} = userInfo;

			const {
				firstName,
				lastName,
				jobTitle,
				department,
				officeLocation,
				avatarId,
				pronouns
			} = userIdentity;

			return {
				firstName,
				lastName,
				jobTitle,
				department,
				officeLocation,
				profileImageUrl: avatarId
					? getUserProfileImageUrl(avatarId)
					: null,
				pronouns
			};
		case "contactDetails":
			return convertFullDetailsToAlwaysAvailableDetails(userInfo.contactDetails);
		default:
			never(userInfo);
	}
}

function convertFullDetailsToAlwaysAvailableDetails(contactDetails: WorkspaceContactDetailsModel): AlwaysAvailableUserDetails {
	const {
		firstName,
		lastName,
		jobTitle,
		department,
		officeLocation,
		photoUrl,
		pronouns
	} = contactDetails;

	return {
		firstName,
		lastName,
		jobTitle,
		department,
		officeLocation,
		profileImageUrl: photoUrl,
		pronouns
	};
}